import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2, Trophy } from "lucide-react";
import { Toaster } from "../components/ui/sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: false });

export default function PublicAttendancePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/attendance/${token}`);
      setInfo(data);
      setCheckedIds(new Set(data.already_checked_in || []));
    } catch (e) {
      setError(e?.response?.data?.detail || "QR non valido o scaduto");
    }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const checkin = async (player) => {
    setSubmitting(player.id);
    try {
      await publicApi.post(`/public/attendance/${token}/checkin`, { training_id: info.training.id, player_id: player.id });
      setCheckedIds((s) => new Set([...s, player.id]));
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore");
    }
    setSubmitting(null);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Caricamento...</div>;
  if (error && !info) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="p-10 max-w-md text-center">
        <AlertTriangle size={48} className="mx-auto text-red-500 mb-3" />
        <h2 className="font-heading text-2xl font-bold">QR non valido</h2>
        <p className="text-slate-500 mt-2">{error}</p>
        <p className="text-xs text-slate-400 mt-3">Chiedi al Mister un nuovo codice</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4">
      <Toaster richColors position="top-right" />
      <div className="max-w-md mx-auto space-y-4">
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-sky-500 flex items-center justify-center"><Trophy size={22} className="text-white" /></div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-xl truncate">{info.team.name}</h1>
              <p className="text-xs text-slate-500">{info.team.category} · {info.training.date}</p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-sky-50 rounded-lg text-sm">
            <strong>Registra presenza all'allenamento:</strong> {info.training.title}
          </div>
        </Card>

        <Card className="p-4 border-slate-200">
          <p className="text-xs text-slate-500 mb-3">Tocca il tuo nome:</p>
          <div className="space-y-2">
            {info.players.map((p) => {
              const checked = checkedIds.has(p.id);
              return (
                <Button
                  key={p.id}
                  onClick={() => !checked && checkin(p)}
                  disabled={checked || submitting === p.id}
                  variant={checked ? "outline" : "default"}
                  className={`w-full justify-start ${checked ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-sky-500 hover:bg-sky-600 text-white"}`}
                  data-testid={`checkin-${p.id}`}
                >
                  {checked ? <CheckCircle2 size={16} className="mr-2" /> : submitting === p.id ? <Loader2 size={16} className="animate-spin mr-2" /> : <span className="w-4 h-4 rounded-full border-2 border-white/50 mr-2" />}
                  {p.jersey_number != null ? `#${p.jersey_number} ` : ""}{p.last_name} {p.first_name}
                </Button>
              );
            })}
          </div>
        </Card>
        <p className="text-center text-xs text-slate-400">{checkedIds.size}/{info.players.length} presenti</p>
      </div>
    </div>
  );
}
